import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { configuratorConfigurationQueryOptions } from "../data/configuratorConfigurationQuery";
import { bindConfiguratorDefinition } from "../engine/definition/bindConfiguratorDefinition";
import type { ConfiguratorDefinition } from "../contract/definition";
import { buildConfigurationSelectionModel, type ConfigurationSelectionModel } from "../engine/selection";
import {
  deriveConfiguratorSteps,
  isConfigurationComplete,
  type ResolvedStepModel,
} from "../engine/deriveConfiguratorModel";
import type { ConfiguratorConfiguration } from "../contract/configuration";
import type { ConfiguratorRuntimeContext } from "../contract/context";
import type { ConfiguratorInitialExpansion } from "../data/configuratorConfigurationQuery";

export interface ConfiguratorViewModel {
  isLoading: boolean;
  error: Error | null;
  configuration: ConfiguratorConfiguration | null;
  context: ConfiguratorRuntimeContext | null;
  steps: readonly ResolvedStepModel[];
  selections: ConfigurationSelectionModel;
  activeStepIndex: number;
  complete: boolean;
  initialExpansion?: ConfiguratorInitialExpansion;
}

export interface ReadyConfiguratorViewModel extends ConfiguratorViewModel {
  isLoading: false;
  error: null;
  configuration: ConfiguratorConfiguration;
  context: ConfiguratorRuntimeContext;
}

export const isReadyConfiguratorViewModel = (
  viewModel: ConfiguratorViewModel,
): viewModel is ReadyConfiguratorViewModel =>
  !viewModel.isLoading && viewModel.error === null && viewModel.configuration !== null && viewModel.context !== null;

const EMPTY_SELECTIONS: ConfigurationSelectionModel = { steps: [] };

export const useConfiguratorViewModel = (definition: ConfiguratorDefinition): ConfiguratorViewModel => {
  const query = useQuery(configuratorConfigurationQueryOptions(definition.productCode));

  return useMemo(() => {
    if (!query.data) {
      return {
        isLoading: query.isLoading,
        error: query.error,
        configuration: null,
        context: null,
        steps: [],
        selections: EMPTY_SELECTIONS,
        activeStepIndex: 0,
        complete: false,
      };
    }

    try {
      bindConfiguratorDefinition(definition, query.data.configuration);
      const steps = deriveConfiguratorSteps(definition, query.data.configuration);

      return {
        isLoading: false,
        error: null,
        configuration: query.data.configuration,
        context: query.data.context,
        steps,
        selections: buildConfigurationSelectionModel(steps),
        activeStepIndex: Math.max(0, query.data.configuration.currentStepNumber - 1),
        complete: steps.length > 0 && isConfigurationComplete(steps, query.data.configuration),
        initialExpansion: query.data.initialExpansion,
      };
    } catch (error) {
      const definitionError = error instanceof Error ? error : new Error("Configurator definition binding failed.");
      console.error(definitionError);

      return {
        isLoading: false,
        error: definitionError,
        configuration: query.data.configuration,
        context: query.data.context,
        steps: [],
        selections: EMPTY_SELECTIONS,
        activeStepIndex: 0,
        complete: false,
      };
    }
  }, [definition, query.data, query.error, query.isLoading]);
};
